import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CanonicalUrlService {
  private router = inject(Router);
  private document = inject<Document>(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  private baseUrl = environment.production
    ? 'https://kingiftcard.com'
    : 'https://brokerplus.mana-card.com';
  constructor() {
    this.setupCanonicalLinks();
  }

  private setupCanonicalLinks() {
    if (isPlatformBrowser(this.platformId)) {
      // For client-side rendering - subscribe to route changes
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          if (
            event.urlAfterRedirects.includes('redirect') &&
            typeof window !== 'undefined'
          ) {
            window.sessionStorage.removeItem('gateio_oauth_started');
          }
          const pathWithoutQuery = event.urlAfterRedirects.split('?')[0];
          const url = `${this.baseUrl}${pathWithoutQuery}`;
          this.updateCanonicalLink(url);
        });
    } else if (isPlatformServer(this.platformId)) {
      // For server-side rendering - set once based on initial route
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          take(1) // Take only the first NavigationEnd event on the server
        )
        .subscribe((event: NavigationEnd) => {
          const pathWithoutQuery = event.urlAfterRedirects.split('?')[0];
          const url = `${this.baseUrl}${pathWithoutQuery}`;
          this.updateCanonicalLink(url);
        });
    }
  }

  private updateCanonicalLink(url: string) {
    if (isPlatformBrowser(this.platformId)) {
      // Client-side approach
      const existingLink = this.document.head.querySelector(
        'link[rel="canonical"]'
      );

      if (existingLink) {
        existingLink.setAttribute('href', url);
      } else {
        const link = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', url);
        this.document.head.appendChild(link);
      }
    } else if (isPlatformServer(this.platformId)) {
      // Server-side: manage a real <link rel="canonical"> in document head.
      const existingLink = this.document.head.querySelector(
        'link[rel="canonical"]'
      );
      if (existingLink) {
        existingLink.setAttribute('href', url);
      } else {
        const link = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', url);
        this.document.head.appendChild(link);
      }
    }
  }
}
